import React, { useState, useRef, useEffect } from 'react';
import { 
  Bell, 
  Package, 
  AlertTriangle, 
  CreditCard, 
  Info, 
  Check, 
  CheckCheck,
  Clock,
  X,
  ShoppingBag
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useSellerNotifications } from '../hooks/useSellerNotifications';

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllRead, loading } = useSellerNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // all, orders, system
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    
    // Navigate based on type
    // types: new_order, order_status, low_stock, payment, commission, system
    switch (notification.type) {
      case 'new_order':
      case 'order_status':
        if (notification.orderId) {
            // Pass orderId in state to highlight/expand it on the orders page
            navigate('/orders', { state: { highlightOrderId: notification.orderId } });
        } else {
            navigate('/orders');
        }
        break;
      case 'low_stock':
        navigate('/products');
        break;
      case 'payment':
      case 'commission':
        navigate('/earnings');
        break;
      default:
        // For system notifications or others, we might just stay here or go to dashboard
        // If it has a link in data, use it?
        if (notification.link) {
            navigate(notification.link);
        }
        break;
    }
    
    setIsOpen(false);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'new_order':
        return <ShoppingBag size={16} className="text-blue-600" />;
      case 'order_status':
        return <Package size={16} className="text-purple-600" />;
      case 'low_stock':
        return <AlertTriangle size={16} className="text-amber-600" />;
      case 'payment':
      case 'commission':
        return <CreditCard size={16} className="text-green-600" />;
      case 'system':
      default:
        return <Info size={16} className="text-gray-600" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'new_order': return 'bg-blue-50';
      case 'order_status': return 'bg-purple-50';
      case 'low_stock': return 'bg-amber-50';
      case 'payment':
      case 'commission': return 'bg-green-50';
      default: return 'bg-gray-50';
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'orders') return ['new_order', 'order_status'].includes(n.type);
    if (filter === 'system') return !['new_order', 'order_status'].includes(n.type);
    return true;
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#FF0057] text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white shadow-sm animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
            <h3 className="font-bold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                onClick={markAllRead}
                className="text-xs text-[#FF0057] hover:text-[#D9004A] font-medium flex items-center gap-1"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex gap-2 overflow-x-auto no-scrollbar">
            {['all', 'orders', 'system'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-colors ${
                  filter === f 
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200' 
                    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF0057] mb-2"></div>
                <p className="text-sm">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length > 0 ? (
              <div className="divide-y divide-gray-50">
                {filteredNotifications.map((notification) => (
                  <div 
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors flex gap-3 ${
                      !notification.isRead ? 'bg-blue-50/30' : ''
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getBgColor(notification.type)}`}>
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium truncate ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notification.title}
                        </p>
                        {!notification.isRead && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {notification.message || notification.body}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400">
                        <Clock size={10} />
                        <span>
                          {notification.createdAt ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true }) : 'Just now'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                <Bell size={32} className="mb-2 opacity-20" />
                <p className="text-sm">No notifications yet</p>
              </div>
            )}
          </div>
          
          {/* Footer (Optional) */}
          {/* <div className="p-3 border-t border-gray-100 bg-gray-50 text-center">
            <button className="text-xs text-gray-500 hover:text-gray-900 font-medium">
              View All Notifications
            </button>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
